import React, {useEffect, useRef, useState} from 'react';
import {Link, useLocation, useNavigate} from "react-router-dom";
import {useAuth} from "../auth/AuthContext";
import axios from 'axios';
import {API_ENDPOINT} from '../config';

const LoginPage = ({ bodyClass }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [needsConfirm, setNeedsConfirm] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const {auth, setAuthData} = useAuth();

    const [altchaVerified, setAltchaVerified] = useState(false);
    const [altchaPayload, setAltchaPayload] = useState(null);
    const altchaRef = useRef(null);

    useEffect(() => {
        if (localStorage.getItem('authToken')) {
            const redirectAfterLogin = location.state?.from || '/';
            navigate(redirectAfterLogin, { replace: true });
        }
    }, [location, navigate, auth]);

    useEffect(() => {
        const previousClassName = document.body.className;
        document.body.className = bodyClass;
        // restaureaza clasa anterioara la demontare - altfel (HashRouter, fara reload)
        // navigarea catre alta pagina ramane cu tema de login (bug: dashboard aparea pe jumatate light)
        return () => {
            document.body.className = previousClassName;
        };
    }, [bodyClass]);

    useEffect(() => {
        const widget = altchaRef.current;

        if (!widget) {
            return;
        }

        const handleStateChange = (event) => {
            const state = event.detail?.state;

            if (state === 'verified') {
                setAltchaVerified(true);

                const input = widget.querySelector('input[name="altcha"]');
                setAltchaPayload(input?.value || null);
            } else {
                setAltchaVerified(false);
                setAltchaPayload(null);
            }
        };

        widget.addEventListener('statechange', handleStateChange);

        return () => {
            widget.removeEventListener('statechange', handleStateChange);
        };
    }, []);

    const handleLogin = async () => {
        setError(null);
        setNeedsConfirm(false);

        if (!altchaVerified || !altchaPayload) {
            setError('Te rog să finalizezi verificarea CAPTCHA.');
            return;
        }

        setLoading(true);

        try {
            const response = await axios.post(`${API_ENDPOINT}/login_check`, {
                username,
                password,
                altcha: altchaPayload,
            });

            const {token, roles} = response.data;

            setAuthData(token, roles);
            localStorage.setItem('authToken', token);
            localStorage.setItem('roles', JSON.stringify(roles));

        } catch (error) {
            console.error('Login failed', error);

            if (error.response?.status === 401) {
                const msg = error.response?.data?.message || '';

                if (msg.includes('not confirmed')) {
                    setNeedsConfirm(true);
                    setError(
                        'Contul nu este confirmat. Puteți solicita un cod nou de confirmare.'
                    );
                } else {
                    setError('Utilizator sau parolă greșită.');
                }
            } else if (error.response?.status === 400) {
                setError(
                    error.response?.data?.message ||
                    'Verificarea CAPTCHA a eșuat.'
                );
            } else {
                setError('Eroare de conexiune la server. Încearcă din nou.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-box">
            <div className="login-logo">
                <a href="https://www.webnou.ro">App.<b>WebNou</b>.Ro</a>
            </div>

            <div className="card">
                <div className="card-body login-card-body">
                    <p className="login-box-msg">Sign in to start your session</p>
                    {error && (
                        <div className="alert alert-danger py-2" role="alert">{error}</div>
                    )}
                    {needsConfirm && (
                        <button
                            type="button"
                            className="btn btn-warning btn-block mb-3"
                            onClick={() => navigate('/sign-up', {state: {confirmEmail: username}})}
                        >
                            <i className="fas fa-envelope-open-text mr-1"></i> Confirmă contul
                        </button>
                    )}
                    <form onSubmit={(e) => {
                        e.preventDefault();
                        handleLogin();
                    }}>
                        <div className="input-group mb-3">
                            <input type="email" className="form-control" placeholder="Email"
                                   onChange={(e) => setUsername(e.target.value)}
                            />
                            <div className="input-group-append">
                                <div className="input-group-text">
                                    <span className="fas fa-envelope"></span>
                                </div>
                            </div>
                        </div>
                        <div className="input-group mb-3">
                            <input type="password" className="form-control" placeholder="Password"
                                   onChange={(e) => setPassword(e.target.value)}
                            />
                            <div className="input-group-append">
                                <div className="input-group-text">
                                    <span className="fas fa-lock"></span>
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-8">
                                <div className="icheck-primary">
                                    <input type="checkbox" id="remember"/>&nbsp;
                                    <label htmlFor="remember">
                                        Remember Me
                                    </label>
                                </div>
                            </div>
                            <div className="col-4">
                                <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
                                    {loading ? <i className="fas fa-spinner fa-spin"></i> : 'Sign In'}
                                </button>
                            </div>

                        </div>
                    </form>
                    <br/>
                    <p className="mb-0">
                        <altcha-widget
                            ref={altchaRef}
                            challenge={`${API_ENDPOINT}/captcha/challenge`}
                            name="altcha"
                        />
                    </p>
                    <p className="mb-0">
                        <Link to="/" className="text-center">Home page</Link>
                    </p>
                    <p className="mb-0">
                        <Link to="/sign-up" className="text-center">Creează un cont nou</Link>
                    </p>
                </div>

            </div>
        </div>
    );
};

export default LoginPage;
